import React from 'react';
import { Box } from '@mui/material';
import AnalyticsObjectHeader from './AnalyticsObjectHeader';
import { getWorkspaceTokens } from './workspaceStyles';
import {
  buildResponsiveFillFlex,
  buildResponsiveFillHeight,
  buildResponsiveFillMaxHeight,
} from './workspaceResponsiveSizing';

const toCssLength = (value) => {
  if (typeof value === 'number') return `${value}px`;
  if (typeof value === 'string') return value;
  return undefined;
};

const getBreakpointKeys = (...values) => {
  const keys = values
    .filter((value) => value && typeof value === 'object' && !Array.isArray(value))
    .flatMap((value) => Object.keys(value));
  return Array.from(new Set(keys));
};

const getBreakpointValue = (value, key) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  return value[key];
};

const buildEffectiveWidth = (width, maxWidth) => {
  const cssWidth = toCssLength(width);
  const cssMaxWidth = toCssLength(maxWidth);

  if (cssWidth && cssMaxWidth) return `min(${cssWidth}, ${cssMaxWidth})`;
  return cssWidth || cssMaxWidth;
};

const buildContentStartInset = (contentWidth, contentMaxWidth) => {
  const breakpointKeys = getBreakpointKeys(contentWidth, contentMaxWidth);

  if (breakpointKeys.length > 0) {
    return breakpointKeys.reduce((acc, key) => {
      const effectiveWidth = buildEffectiveWidth(
        getBreakpointValue(contentWidth, key) ?? contentWidth,
        getBreakpointValue(contentMaxWidth, key) ?? contentMaxWidth
      );

      acc[key] = effectiveWidth
        ? `max(0px, calc((100% - ${effectiveWidth}) / 2))`
        : 0;
      return acc;
    }, {});
  }

  const effectiveWidth = buildEffectiveWidth(contentWidth, contentMaxWidth);
  return effectiveWidth
    ? `max(0px, calc((100% - ${effectiveWidth}) / 2))`
    : 0;
};

function AnalyticsPanel({
  title,
  description,
  eyebrow,
  icon,
  actions,
  filters,
  variant = 'panel',
  density = 'compact',
  width = '100%',
  maxWidth,
  minHeight,
  fill = false,
  scroll = 'hidden',
  contentWidth,
  contentMaxWidth,
  contentAlign = 'stretch',
  headerContentWidth,
  headerContentMaxWidth,
  headerContentAlign,
  headerLayout = 'content',
  children,
  sx,
  contentSx,
  headerSx,
  style,
}) {
  const panelMaxWidth = maxWidth || (variant === 'table' ? '100%' : '100%');
  const contentStartHeader = headerLayout === 'contentStart';
  const headerStartInset = contentStartHeader
    ? buildContentStartInset(contentWidth, contentMaxWidth)
    : 0;
  const resolvedHeaderContentWidth = contentStartHeader
    ? (headerContentWidth ?? '100%')
    : (headerContentWidth ?? contentWidth);
  const resolvedHeaderContentMaxWidth = contentStartHeader
    ? (headerContentMaxWidth ?? '100%')
    : (headerContentMaxWidth ?? contentMaxWidth);
  const resolvedHeaderContentAlign = contentStartHeader
    ? (headerContentAlign ?? 'stretch')
    : (headerContentAlign ?? contentAlign);

  return (
    <Box
      data-testid="analytics-panel"
      data-variant={variant}
      style={style}
      sx={(theme) => {
        const tokens = getWorkspaceTokens(theme);
        return {
          width,
          maxWidth: panelMaxWidth,
          minWidth: 0,
          minHeight,
          height: buildResponsiveFillHeight(fill),
          maxHeight: buildResponsiveFillMaxHeight(fill),
          flex: buildResponsiveFillFlex(fill),
          alignSelf: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          borderRadius: `${tokens.radius}px`,
          overflow: fill ? 'hidden' : 'visible',
          ...sx,
        };
      }}
    >
      {(title || description || actions || filters) && (
        <AnalyticsObjectHeader
          title={title}
          description={description}
          eyebrow={eyebrow}
          icon={icon}
          actions={actions}
          filters={filters}
          variant={variant}
          density={density}
          contentWidth={resolvedHeaderContentWidth}
          contentMaxWidth={resolvedHeaderContentMaxWidth}
          contentAlign={resolvedHeaderContentAlign}
          sx={{
            boxSizing: 'border-box',
            pl: headerStartInset,
            ...headerSx,
          }}
        />
      )}
      <Box
        data-testid="analytics-panel-content"
        sx={{
          width: contentWidth,
          maxWidth: contentMaxWidth,
          alignSelf: contentAlign,
          minWidth: 0,
          minHeight: 0,
          height: buildResponsiveFillHeight(fill),
          maxHeight: buildResponsiveFillMaxHeight(fill),
          flex: buildResponsiveFillFlex(fill),
          position: 'relative',
          overflow: scroll,
          ...contentSx,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default AnalyticsPanel;
